import React, { useState, useRef, useCallback, useMemo } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { Modal, Button, Row, Col, App as AntApp, Space, Tooltip } from "antd";
import axios from "axios";
import { handleMedia } from "../../config/api";
import { Encrypt } from "../../utils/encryption";
import {
  cdnUrlForStorageKey,
  mediaPath,
  cdnSafeMediaPathSegment,
} from "../../config/mediaPaths";
import {
  fetchManifestForPath,
  invalidateManifestFetchCacheForPath,
  preferNewerOrRicherManifest,
  putManifestWithPresignedUrl,
} from "../../utils/mediaManifest";
import { OPERATION_TYPE } from "../../config/mediaOperationTypes";
import { setGalleryManifestForPath } from "../../store/gallerySlice";
import ImagePreviewModal from "../../components/ImagePreviewModal";
import { isHeicFile } from "../../utils/heicConversion";
import { putPresignedObject } from "../../utils/putPresignedObject";

const VALID_TYPES = ["image/jpg", "image/jpeg", "image/png", "image/webp"];
const IMAGE_UPLOAD_ACCEPT = "image/jpeg,image/png,image/webp";
const MAX_SIZE = 5 * 1024 * 1024;
const TILE = 64;

function basenameOnly(key) {
  const k = String(key || "");
  const i = k.lastIndexOf("/");
  return i >= 0 ? k.slice(i + 1) : k;
}

/**
 * Single-image picker for biz hero image (bizImageKey).
 *
 * Props:
 *   token       — auth token
 *   hubId       — hub PK
 *   bizId       — biz/store ID (undefined in create mode → component is hidden)
 *   value       — current bizImageKey string
 *   onChange    — (newKey: string) => void  — called with basename imageKey
 *   disabled    — optional
 */
export default function BizImageUpload({ token, hubId, bizId, value, onChange, disabled = false }) {
  const dispatch = useDispatch();
  const { message } = AntApp.useApp();
  const store = useStore();
  const fileInputRef = useRef(null);

  const [uploading, setUploading] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [manifestStatus, setManifestStatus] = useState("idle");
  const [manifestImages, setManifestImages] = useState([]);
  const [selectedGalleryKey, setSelectedGalleryKey] = useState(null);
  const [previewState, setPreviewState] = useState(null);
  const [previewModalKey, setPreviewModalKey] = useState(0);
  const [imgError, setImgError] = useState(false);

  const bizGalleryPrefix = useMemo(
    () => (hubId && bizId ? mediaPath({ entity: "biz", fileType: "media", hubId, bizId }) : ""),
    [hubId, bizId]
  );

  const bizGallerySlot = useSelector((state) =>
    bizGalleryPrefix ? state.gallery.manifestByPathPrefix[bizGalleryPrefix] : undefined
  );

  const refreshManifest = useCallback(async () => {
    if (!hubId || !bizId || !token) return;
    const prefix = mediaPath({ entity: "biz", fileType: "media", hubId, bizId });
    setManifestStatus("loading");
    try {
      invalidateManifestFetchCacheForPath(prefix);
      const remote = await fetchManifestForPath(prefix, { token, preferOrigin: true });
      const prev = store.getState().gallery.manifestByPathPrefix[prefix]?.manifest ?? { version: 1, images: [] };
      const chosen = preferNewerOrRicherManifest(prev, remote);
      dispatch(setGalleryManifestForPath({ pathPrefix: prefix, manifest: chosen, bumpImageCache: true }));
      setManifestImages(Array.isArray(chosen.images) ? chosen.images : []);
    } finally {
      setManifestStatus("ready");
    }
  }, [hubId, bizId, token, dispatch, store]);

  // Sync from Redux when slot loads
  React.useEffect(() => {
    if (bizGallerySlot?.manifest) {
      setManifestImages(bizGallerySlot.manifest.images || []);
      setManifestStatus("ready");
    } else if (hubId && bizId && token && manifestStatus === "idle") {
      refreshManifest();
    }
  }, [bizGallerySlot, hubId, bizId, token, manifestStatus, refreshManifest]);

  // Reset imgError when value changes
  React.useEffect(() => { setImgError(false); }, [value]);

  const currentUrl = useMemo(() => {
    if (!value || !hubId || !bizId) return null;
    return cdnUrlForStorageKey(`${bizGalleryPrefix}/${basenameOnly(value)}`);
  }, [value, hubId, bizId, bizGalleryPrefix]);

  const noGalleryImages = manifestStatus === "ready" && manifestImages.length === 0;

  const handleAutoUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (!files.length) return;

    let file = files[0]; // biz image: single
    if (isHeicFile(file)) {
      message.error("HEIC/HEIF images are not supported. Use JPG, PNG, or WebP.");
      return;
    }
    if (!VALID_TYPES.includes(file.type)) {
      message.warning("Unsupported type — use JPG, PNG, or WebP.");
      return;
    }
    if (file.size > MAX_SIZE) {
      message.warning("Image exceeds 5MB limit.");
      return;
    }
    if (!token || !hubId || !bizId) {
      message.warning("Save the store first to upload a biz image.");
      return;
    }

    try {
      setUploading(true);
      const itemData = {
        hubId,
        itemId: "biz-gallery",
        storeId: cdnSafeMediaPathSegment(bizId),
        images: [{ fileName: file.name }],
        gallerySegment: "biz",
        operationType: OPERATION_TYPE.PUT_BIZ_PRESIGNED_URL,
      };
      const response = await axios.post(
        handleMedia,
        Encrypt({ token, action: "uploadImages", itemData }),
        { headers: { "Content-Type": "application/json" } }
      );
      const result = response.data;
      if (!result.success) { message.error(result.message || "Upload failed."); return; }

      const uploaded = result.uploadedImages?.[0];
      if (!uploaded) { message.error("No presigned URL returned."); return; }

      await putPresignedObject(
        uploaded.presignedUrl,
        file,
        uploaded.contentType || file.type
      );

      const key = basenameOnly(uploaded.imageKey);

      // Build and write updated manifest (same pattern as GallerySegmentPanel)
    const prefix = mediaPath({ entity: "biz", fileType: "media", hubId, bizId });
      const prevManifest = store.getState().gallery.manifestByPathPrefix[prefix]?.manifest
        ?? { version: 1, images: [] };
      const newEntry = { imageKey: key, originalFilename: file.name };
      const mergedImages = [...(prevManifest.images || []).filter((e) => e.imageKey !== key), newEntry];
      const nextManifest = { ...prevManifest, version: (prevManifest.version || 1) + 1, images: mergedImages };
      if (result.manifestPresignedUrl) {
        try {
          await putManifestWithPresignedUrl(result.manifestPresignedUrl, nextManifest);
        } catch (e) {
          message.warning("Image uploaded, but manifest update failed — gallery may not refresh on other devices.");
        }
      }
      dispatch(setGalleryManifestForPath({ pathPrefix: prefix, manifest: nextManifest, bumpImageCache: true }));
      setManifestImages(nextManifest.images);

      if (onChange) onChange(key);
      message.success("Biz image uploaded.");
      // refreshManifest not needed — manifest already written and dispatched above
    } catch (err) {
      message.error(err?.message || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const applyGalleryPick = () => {
    if (!selectedGalleryKey || !onChange) return;
    onChange(basenameOnly(selectedGalleryKey));
    setGalleryOpen(false);
    setSelectedGalleryKey(null);
    message.success("Image selected.");
  };

  // Hidden in create mode (no bizId)
  if (!bizId) return null;

  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8, flexWrap: "wrap" }}>
        {/* Current image thumbnail */}
        {currentUrl && !imgError && (
          <div
            style={{
              width: TILE * 2,
              height: TILE,
              borderRadius: 8,
              overflow: "hidden",
              border: "1px solid #e2e8f0",
              background: "#f8fafc",
              flexShrink: 0,
              cursor: "pointer",
              position: "relative",
            }}
            onClick={() => { setPreviewState({ images: [currentUrl], index: 0 }); setPreviewModalKey(k => k + 1); }}
          >
            <img src={currentUrl} alt="" onError={() => setImgError(true)}
              style={{ width: "100%", height: "100%", objectFit: "contain", background: "#f8fafc" }} />
            {!disabled && (
              <button type="button"
                onClick={(e) => { e.stopPropagation(); if (onChange) onChange(""); }}
                style={{
                  position: "absolute", top: 3, right: 3,
                  width: 18, height: 18, padding: 0, fontSize: 11, lineHeight: 1,
                  borderRadius: "50%", border: "none",
                  background: "rgba(0,0,0,0.6)", color: "#fff", cursor: "pointer",
                }}>{"×"}</button>
            )}
          </div>
        )}

        {/* Upload button */}
        {!disabled && (
          <Tooltip title="Upload image (JPG, PNG, WebP · max 5MB)">
            <label style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              width: TILE, height: TILE, flexShrink: 0,
              border: "1px dashed var(--zh-border-md)", borderRadius: 8,
              background: uploading ? "var(--zh-muted)" : "var(--zh-surface)",
              cursor: uploading ? "not-allowed" : "pointer",
              color: uploading ? "var(--zh-text-muted)" : "var(--zh-brand-primary)",
              transition: "background 0.15s",
            }}>
              <input
                ref={fileInputRef}
                type="file"
                accept={IMAGE_UPLOAD_ACCEPT}
                onChange={handleAutoUpload}
                disabled={uploading}
                style={{ display: "none" }}
              />
              {uploading ? (
                <span style={{ fontSize: 11, color: "#94a3b8" }}>{"..."}</span>
              ) : (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                  <circle cx="12" cy="13" r="4"/>
                </svg>
              )}
            </label>
          </Tooltip>
        )}

        {/* Gallery button */}
        {!disabled && (
          <Tooltip title={noGalleryImages ? "No biz images yet — upload one first" : "Pick from gallery"}>
            <button
              type="button"
              disabled={noGalleryImages || manifestStatus === "loading"}
              onClick={() => { setSelectedGalleryKey(null); setGalleryOpen(true); refreshManifest(); }}
              style={{
                display: "flex", alignItems: "center", justifyContent: "center",
                width: TILE, height: TILE, flexShrink: 0,
                border: "1px solid #e2e8f0", borderRadius: 8,
                background: noGalleryImages ? "#f1f5f9" : "#fafafa",
                cursor: noGalleryImages ? "not-allowed" : "pointer",
                color: noGalleryImages ? "#cbd5e1" : "#475569",
                padding: 0, transition: "background 0.15s",
              }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                <circle cx="8.5" cy="8.5" r="1.5"/>
                <polyline points="21 15 16 10 5 21"/>
              </svg>
            </button>
          </Tooltip>
        )}
      </div>

      {/* Gallery pick modal */}
      <Modal
        title="Pick biz image"
        open={galleryOpen}
        onCancel={() => { setGalleryOpen(false); setSelectedGalleryKey(null); }}
        width="min(100%, 440px)"
        footer={
          <Space style={{ width: "100%", justifyContent: "flex-end" }}>
            <Button onClick={() => { setGalleryOpen(false); setSelectedGalleryKey(null); }}>Cancel</Button>
            <Button type="primary" disabled={!selectedGalleryKey} onClick={applyGalleryPick}>Use this image</Button>
          </Space>
        }
      >
        {manifestStatus === "loading" && manifestImages.length === 0 ? (
          <div style={{ color: "#888", fontSize: 13 }}>Loading gallery…</div>
        ) : manifestImages.length === 0 ? (
          <div style={{ color: "#888", fontSize: 13 }}>No biz images yet.</div>
        ) : (
          <Row gutter={[8, 8]}>
            {manifestImages.map((entry) => {
              const key = entry.imageKey;
              const url = cdnUrlForStorageKey(`${bizGalleryPrefix}/${basenameOnly(key)}`);
              const isSelected = selectedGalleryKey === key;
              const isCurrent = value && basenameOnly(value) === basenameOnly(key);
              return (
                <Col xs={8} sm={6} key={key}>
                  <button type="button"
                    onClick={() => setSelectedGalleryKey(isSelected ? null : key)}
                    style={{
                      border: isSelected ? "2px solid var(--zh-brand-primary)" : isCurrent ? "2px solid var(--zh-success)" : "1px solid var(--zh-border-md)",
                      borderRadius: 8, padding: 0, overflow: "hidden",
                      background: "var(--zh-surface)", cursor: "pointer",
                      width: "100%", aspectRatio: "1",
                    }}
                  >
                    <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  </button>
                </Col>
              );
            })}
          </Row>
        )}
      </Modal>

      <ImagePreviewModal
        key={previewModalKey}
        visible={!!previewState}
        images={previewState?.images || []}
        initialIndex={0}
        onClose={() => setPreviewState(null)}
      />
    </div>
  );
}
